import { useEffect, useState } from 'react';
import {
  Camera, Video, Radio,
  Eye, Trash2,
  X, FileText, Calendar,
  Clock, MapPin, Users,
  GraduationCap, Star, MessageSquare,
  Mail, Phone, Briefcase, Building2,
  UserCheck, Search, LayoutDashboard,
  Inbox, Filter, ChevronDown,
} from 'lucide-react';
import './AdminRequests.css';

const API_URL    = 'http://localhost/reactwebmedia/backend/get_requests.php';
const DETAIL_URL = 'http://localhost/reactwebmedia/backend/get_request_by_id.php';
const DELETE_URL = 'http://localhost/reactwebmedia/backend/delete_request.php';

const SERVICE_META = {
  photography:    { Icon: Camera, label: 'Photography',    color: '#f5a623' },
  videography:    { Icon: Video,  label: 'Videography',    color: '#e85d5d' },
  live_streaming: { Icon: Radio,  label: 'Live Streaming', color: '#5de8c8' },
};

/* ─── Small reusable components ─────────────────── */
function ServiceBadge({ type }) {
  const { Icon, label, color } = SERVICE_META[type];
  return (
    <span className="ar-svc-badge" style={{ '--svc-color': color }}>
      <Icon size={10} strokeWidth={2.5} /> {label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, accent, delay }) {
  return (
    <div className="ar-stat" style={{ '--delay': delay, '--accent': accent }}>
      <div className="ar-stat-icon"><Icon size={20} strokeWidth={1.8} /></div>
      <div>
        <div className="ar-stat-value">{value}</div>
        <div className="ar-stat-label">{label}</div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="ar-detail-row">
      <span className="ar-detail-icon"><Icon size={13} strokeWidth={2} /></span>
      <div>
        <div className="ar-detail-label">{label}</div>
        <div className="ar-detail-value">{value}</div>
      </div>
    </div>
  );
}

/* ─── Booking Card ───────────────────────────────── */
function BookingCard({ req, isActive, onView, onDelete, formatTime }) {
  const services = ['photography', 'videography', 'live_streaming'].filter(
    s => Number(req[s]) === 1
  );

  const initials = (req.requested_person || '?')
    .split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();

  return (
    <div
      className={`ar-card ${isActive ? 'ar-card--active' : ''}`}
      style={{ '--idx': req._idx }}
    >
      {/* Top accent bar colored by first service */}
      <div className="ar-card-topbar"
        style={{ '--bar-color': services[0] ? SERVICE_META[services[0]].color : 'var(--yellow)' }}
      />

      {/* Card header */}
      <div className="ar-card-header">
        <div className="ar-card-avatar">{initials}</div>
        <div className="ar-card-meta">
          <div className="ar-card-name">{req.requested_person}</div>
          <div className="ar-card-side">{req.request_side}</div>
        </div>
        <div className="ar-card-id">#{String(req.id).padStart(4,'0')}</div>
      </div>

      {/* Event info */}
      <div className="ar-card-event">{req.event_name}</div>

      <div className="ar-card-details">
        <span className="ar-card-detail">
          <Calendar size={11} /> {req.event_date}
        </span>
        {req.event_time && (
          <span className="ar-card-detail">
            <Clock size={11} /> {formatTime(req.event_time)}
          </span>
        )}
        {req.location && (
          <span className="ar-card-detail">
            <MapPin size={11} /> {req.location}
          </span>
        )}
      </div>

      {/* Attendees row */}
      {(req.staff_count || req.student_count) && (
        <div className="ar-card-attendees">
          {req.staff_count > 0 && (
            <span className="ar-card-attendee">
              <Users size={11} /> {req.staff_count} Staff
            </span>
          )}
          {req.student_count > 0 && (
            <span className="ar-card-attendee">
              <GraduationCap size={11} /> {req.student_count} Students
            </span>
          )}
        </div>
      )}

      {/* Services */}
      {services.length > 0 && (
        <div className="ar-card-services">
          {services.map(s => <ServiceBadge key={s} type={s} />)}
        </div>
      )}

      {/* Footer actions */}
      <div className="ar-card-footer">
        <span className="ar-card-email">
          <Mail size={11} /> {req.email}
        </span>
        <div className="ar-card-actions">
          <button className="ar-card-btn ar-card-btn--view" onClick={() => onView(req.id)}>
            <Eye size={13} /> View
          </button>
          <button className="ar-card-btn ar-card-btn--delete" onClick={() => onDelete(req.id)}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
export default function AdminRequests() {
  const [requests,        setRequests]       = useState([]);
  const [loading,         setLoading]        = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showPanel,       setShowPanel]      = useState(false);
  const [search,          setSearch]         = useState('');
  const [filterSvc,       setFilterSvc]      = useState('all');
  const [deleteConfirm,   setDeleteConfirm]  = useState(null);
  const [panelAnim,       setPanelAnim]      = useState(false);

  useEffect(() => {
    fetch(API_URL)
      .then(r => r.json())
      .then(d => { if (d.success) setRequests(d.data); })
      .finally(() => setLoading(false));
  }, []);

  const handleView = async (id) => {
    const res  = await fetch(`${DETAIL_URL}?id=${id}`);
    const data = await res.json();
    if (data.success) {
      setSelectedRequest(data.data);
      setPanelAnim(false);
      setTimeout(() => { setShowPanel(true); setPanelAnim(true); }, 10);
    }
  };

  const handleDelete = async (id) => {
    const res  = await fetch(DELETE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    const data = await res.json();
    if (data.success) {
      setRequests(prev => prev.filter(r => r.id !== id));
      if (selectedRequest?.id === id) { setShowPanel(false); setSelectedRequest(null); }
    }
    setDeleteConfirm(null);
  };

  const formatTime = (t) => {
    if (!t) return '—';
    const [h, m] = t.split(':');
    const h12 = parseInt(h) % 12 || 12;
    return `${h12}:${m} ${parseInt(h) >= 12 ? 'PM' : 'AM'}`;
  };

  const total   = requests.length;
  const photoN  = requests.filter(r => Number(r.photography)    === 1).length;
  const videoN  = requests.filter(r => Number(r.videography)    === 1).length;
  const streamN = requests.filter(r => Number(r.live_streaming) === 1).length;

  const filtered = requests
    .filter(r =>
      [r.event_name, r.requested_person, r.email, r.request_side]
        .some(f => f?.toLowerCase().includes(search.toLowerCase()))
    )
    .filter(r => {
      if (filterSvc === 'all') return true;
      return Number(r[filterSvc]) === 1;
    })
    .map((r, i) => ({ ...r, _idx: i }));

  return (
    <div className="ar-root">
      <div className="ar-bg" />
      <div className="ar-overlay" />

      {/* Film strips */}
      <div className="ar-film ar-film-left">
        {Array.from({length:18}).map((_,i) => <div key={i} className="ar-film-hole" />)}
      </div>
      <div className="ar-film ar-film-right">
        {Array.from({length:18}).map((_,i) => <div key={i} className="ar-film-hole" />)}
      </div>

      {/* Particles */}
      <div className="ar-particles">
        {Array.from({length:10}).map((_,i) => <div key={i} className={`ar-particle arp${i+1}`} />)}
        <div className="ar-orb ar-orb1" />
        <div className="ar-orb ar-orb2" />
      </div>

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="ar-confirm-backdrop">
          <div className="ar-confirm">
            <div className="ar-confirm-icon"><Trash2 size={28} /></div>
            <h3>Delete Request?</h3>
            <p>This action cannot be undone. The booking request will be permanently removed.</p>
            <div className="ar-confirm-btns">
              <button className="ar-confirm-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="ar-confirm-delete" onClick={() => handleDelete(deleteConfirm)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="ar-layout">
        <div className={`ar-main ${showPanel ? 'panel-open' : ''}`}>

          {/* ── Header ── */}
          <div className="ar-header">
            <div>
              <div className="ar-header-badge">
                <LayoutDashboard size={12} /> Admin Dashboard
              </div>
              <h1 className="ar-title">
                Booking <span className="ar-title-accent">Requests</span>
              </h1>
            </div>
            <div className="ar-header-controls">
              {/* Search */}
              <div className="ar-search-wrap">
                <Search size={14} className="ar-search-icon" />
                <input className="ar-search"
                  placeholder="Search events, people…"
                  value={search}
                  onChange={e => setSearch(e.target.value)} />
              </div>
              {/* Filter dropdown */}
              <div className="ar-filter-wrap">
                <Filter size={13} className="ar-filter-icon" />
                <select className="ar-filter"
                  value={filterSvc}
                  onChange={e => setFilterSvc(e.target.value)}>
                  <option value="all">All Services</option>
                  <option value="photography">Photography</option>
                  <option value="videography">Videography</option>
                  <option value="live_streaming">Live Streaming</option>
                </select>
                <ChevronDown size={13} className="ar-filter-chevron" />
              </div>
            </div>
          </div>

          {/* ── Stats ── */}
          <div className="ar-stats">
            <StatCard icon={Inbox}  label="Total Requests" value={total}   accent="#FFEA00" delay="0s"    />
            <StatCard icon={Camera} label="Photography"    value={photoN}  accent="#f5a623" delay="0.07s" />
            <StatCard icon={Video}  label="Videography"    value={videoN}  accent="#e85d5d" delay="0.14s" />
            <StatCard icon={Radio}  label="Live Streaming" value={streamN} accent="#5de8c8" delay="0.21s" />
          </div>

          {/* ── Results count ── */}
          {!loading && (
            <div className="ar-results-bar">
              <span className="ar-results-count">
                {filtered.length} {filtered.length === 1 ? 'request' : 'requests'} found
              </span>
              {(search || filterSvc !== 'all') && (
                <button className="ar-results-clear"
                  onClick={() => { setSearch(''); setFilterSvc('all'); }}>
                  Clear filters ×
                </button>
              )}
            </div>
          )}

          {/* ── Card Grid ── */}
          {loading ? (
            <div className="ar-loading">
              <div className="ar-loading-spinner" />
              <span>Loading requests…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="ar-empty">
              <FileText size={44} strokeWidth={1} />
              <p>No requests found</p>
              <span>Try adjusting your search or filter</span>
            </div>
          ) : (
            <div className="ar-grid">
              {filtered.map(req => (
                <BookingCard
                  key={req.id}
                  req={req}
                  isActive={selectedRequest?.id === req.id}
                  onView={handleView}
                  onDelete={id => setDeleteConfirm(id)}
                  formatTime={formatTime}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Side Panel ── */}
        {showPanel && selectedRequest && (
          <div className={`ar-panel ${panelAnim ? 'ar-panel--in' : ''}`}>
            <div className="ar-panel-header">
              <div>
                <div className="ar-panel-badge">Booking #{selectedRequest.id}</div>
                <h2 className="ar-panel-title">Request Details</h2>
              </div>
              <button className="ar-panel-close" onClick={() => setShowPanel(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="ar-panel-body">
              <div className="ar-panel-section">
                <div className="ar-panel-section-title">Requester</div>
                <DetailRow icon={Building2} label="Request Side" value={selectedRequest.request_side} />
                <DetailRow icon={UserCheck} label="Person"       value={selectedRequest.requested_person} />
                <DetailRow icon={Briefcase} label="Designation"  value={selectedRequest.designation} />
                <DetailRow icon={Mail}      label="Email"        value={selectedRequest.email} />
                <DetailRow icon={Phone}     label="Phone"        value={selectedRequest.phone} />
              </div>
              <div className="ar-panel-section">
                <div className="ar-panel-section-title">Event</div>
                <DetailRow icon={FileText}      label="Event Name" value={selectedRequest.event_name} />
                <DetailRow icon={Calendar}      label="Date"       value={selectedRequest.event_date} />
                <DetailRow icon={Clock}         label="Time"       value={formatTime(selectedRequest.event_time)} />
                <DetailRow icon={MapPin}        label="Location"   value={selectedRequest.location} />
                <DetailRow icon={Users}         label="Staff"      value={selectedRequest.staff_count} />
                <DetailRow icon={GraduationCap} label="Students"   value={selectedRequest.student_count} />
                <DetailRow icon={Star}          label="Invitees"   value={selectedRequest.notable_invitees} />
              </div>
              <div className="ar-panel-section">
                <div className="ar-panel-section-title">Services</div>
                <div className="ar-panel-services">
                  {Number(selectedRequest.photography)    === 1 && <ServiceBadge type="photography" />}
                  {Number(selectedRequest.videography)    === 1 && <ServiceBadge type="videography" />}
                  {Number(selectedRequest.live_streaming) === 1 && <ServiceBadge type="live_streaming" />}
                </div>
              </div>
              {selectedRequest.remarks && (
                <div className="ar-panel-section">
                  <div className="ar-panel-section-title">Remarks</div>
                  <div className="ar-panel-remarks">
                    <MessageSquare size={13} className="ar-panel-remarks-icon" />
                    <p>{selectedRequest.remarks}</p>
                  </div>
                </div>
              )}
              <button className="ar-btn-panel-delete"
                onClick={() => setDeleteConfirm(selectedRequest.id)}>
                <Trash2 size={14} /> Delete This Request
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}